import { Box, ButtonBase, Paper, Typography, useTheme } from "@mui/material";
import React from "react";

/** Shared chrome for the Flow Step Debug page: the wizard tab bar and its section cards. */

export const FLOW_STEP_DEBUG_STAGES = [
    "Configure",
    "Skill Instructions",
    "Evaluation",
] as const;

export type FlowStepDebugStage = (typeof FLOW_STEP_DEBUG_STAGES)[number];

/** Width of the chevron notch cut into the right edge of each stage. */
const CHEVRON_WIDTH = 18;

const chevronClip = (isFirst: boolean) =>
    `polygon(0 0, calc(100% - ${CHEVRON_WIDTH}px) 0, 100% 50%, calc(100% - ${CHEVRON_WIDTH}px) 100%, 0 100%${
        isFirst ? "" : `, ${CHEVRON_WIDTH}px 50%`
    })`;

export function WizardTabs({
    stages = FLOW_STEP_DEBUG_STAGES,
    activeStage,
    reachableStages,
    onStageChange,
}: {
    stages?: readonly FlowStepDebugStage[];
    activeStage: FlowStepDebugStage;
    /** Stages the user has unlocked; the rest render disabled. */
    reachableStages: readonly FlowStepDebugStage[];
    onStageChange: (stage: FlowStepDebugStage) => void;
}) {
    const theme = useTheme();

    return (
        <Box
            role="tablist"
            aria-label="Flow step debug stages"
            sx={{
                bgcolor: theme.palette.gray[100],
                borderRadius: "10px 28px 28px 10px",
                display: "flex",
                overflow: "hidden",
                width: "fit-content",
            }}
        >
            {stages.map((stage, index) => {
                const isActive = stage === activeStage;
                const isReachable = reachableStages.includes(stage);
                return (
                    <ButtonBase
                        key={stage}
                        role="tab"
                        aria-selected={isActive}
                        disabled={!isReachable}
                        onClick={() => onStageChange(stage)}
                        sx={{
                            bgcolor: isActive
                                ? theme.palette.common.white
                                : "transparent",
                            clipPath: isActive ? chevronClip(index === 0) : undefined,
                            color: isActive
                                ? theme.palette.gray[800]
                                : theme.palette.gray[500],
                            fontSize: 15,
                            fontWeight: isActive ? 700 : 500,
                            justifyContent: "center",
                            opacity: isReachable ? 1 : 0.55,
                            pl: index === 0 ? 5 : 6,
                            pr: 6,
                            py: 1.75,
                        }}
                    >
                        {stage}
                    </ButtonBase>
                );
            })}
        </Box>
    );
}

/** Small uppercase card heading used by the left-hand configuration cards. */
export function StepCardLabel({ children }: { children: React.ReactNode }) {
    const theme = useTheme();
    return (
        <Typography
            component="h2"
            sx={{
                color: theme.palette.gray[600],
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
            }}
        >
            {children}
        </Typography>
    );
}

export function SectionCard({
    children,
    sx,
}: {
    children: React.ReactNode;
    sx?: React.ComponentProps<typeof Paper>["sx"];
}) {
    const theme = useTheme();
    return (
        <Paper
            variant="outlined"
            sx={{
                borderColor: theme.palette.gray[200],
                borderRadius: 2.5,
                overflow: "hidden",
                ...sx,
            }}
        >
            {children}
        </Paper>
    );
}

/** Header for a numbered card: "3 · Pick the run" plus a one-line explanation. */
export function SectionCardHeader({
    title,
    subtitle,
    actions,
}: {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
}) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                alignItems: { xs: "flex-start", md: "center" },
                borderBottom: "1px solid",
                borderColor: theme.palette.gray[200],
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 1.5,
                px: 2.5,
                py: 2,
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    component="h2"
                    sx={{ fontSize: 18, fontWeight: 700 }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </Box>
            <Box sx={{ flex: 1 }} />
            {actions}
        </Box>
    );
}

/** Sticky footer strip carrying the current selection and the advance button. */
export function SectionCardFooter({
    summary,
    action,
}: {
    summary: React.ReactNode;
    action: React.ReactNode;
}) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                alignItems: { xs: "stretch", md: "center" },
                borderTop: "1px solid",
                borderColor: theme.palette.gray[200],
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                px: 2.5,
                py: 2,
            }}
        >
            <Typography variant="body2" color="text.secondary">
                {summary}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {action}
        </Box>
    );
}
